import stageKey from "Utils/stageKey";

const DAY = 86400000;
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

/*
 * The skip prompt names the exact tasks rather than describing them.
 *
 * It used to say "skip the tasks whose contact bounced or opted out", which
 * threw away rows the finding had already identified and sent the model
 * searching. /v1/tasks has no working contact-stage filter, so it burned a
 * fan-out of empty queries and gave up without ever showing a list.
 */
const skipPrompt = rows => {
  const ids = rows.map(r => r?.task?.id).filter(Boolean);
  const who = rows
    .map(r => r?.contact?.email || r?.contact?.firstName)
    .filter(Boolean)
    .slice(0, 5)
    .join(", ");
  return (
    `Skip ${plural(ids.length, "task")} by id: ${ids.join(", ")}.` +
    (who ? ` They belong to ${who}.` : "") +
    " Do NOT search for them -- these are the ids. List them back to me and ask me to confirm before skipping anything."
  );
};

// Automated email is the only step type a sequence runs by itself; every other
// task type is a manual step a rep performs (Static/constant.js taskTypeIcon /
// addStepBtn options: manualEmail, linkedin, phone, customTask).
const isManualTaskType = type => type && type !== "email";

// rows: page-local task table rows ({ task, contact, sequenceStatus }). task.taskDue,
// task.replyDateMs and task.createTsMs are pre-normalised to ms by the page (see
// tasks/main.js) so this module stays a pure ms-vs-ms comparison, no Date parsing here.
// actions is optional: { showOverdue, startWith(taskId), filterType(type), askCopilot(text) }.
const taskFindings = (rows = [], { now = Date.now(), liAccounts, actions = {} } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [];
  const out = [];

  const overdue = list.filter(r => r.task?.taskDue && r.task.taskDue < now);
  if (overdue.length) {
    const oldest = Math.floor((now - Math.min(...overdue.map(r => r.task.taskDue))) / DAY);
    out.push({
      tone: "warn",
      key: "overdue",
      label: plural(overdue.length, "task") + " overdue",
      text: `Oldest is ${plural(oldest, "day")} late. Clear these before today's list.`,
      actions: actions.showOverdue ? [{ label: "Show overdue", run: actions.showOverdue }] : []
    });
  }

  const frozen = overdue.filter(
    r => r.sequenceStatus == null && r.task?.campaignSequenceId && isManualTaskType(r.task?.taskType)
  );
  if (frozen.length) {
    out.push({
      tone: "bad",
      key: "frozen_contacts",
      label: plural(frozen.length, "contact") + " frozen behind you",
      text: "Their sequence is live but stuck on your manual step. Clear these first.",
      actions: actions.showOverdue ? [{ label: "Show blocked contacts", run: actions.showOverdue }] : []
    });
  }

  const alreadyReplied = list.filter(
    r => r.task?.replyDateMs && r.task?.createTsMs && r.task.replyDateMs > r.task.createTsMs
  );
  if (alreadyReplied.length) {
    out.push({
      tone: "warn",
      key: "already_replied",
      label: plural(alreadyReplied.length, "task") + " for people who replied",
      text: "They already answered. Clear these instead of sending the next step.",
      actions: actions.askCopilot
        ? [
            {
              label: "Clear these",
              run: () =>
                actions.askCopilot(
                  "List my open tasks whose contact has already replied, and offer to complete them. Ask me to confirm before completing."
                )
            }
          ]
        : []
    });
  }

  if (Array.isArray(liAccounts)) {
    const linkedInTasks = list.filter(
      r => r.task?.taskType === "linkedin" || r.task?.linkedInTaskType
    );
    const hasActiveLi = liAccounts.some(a => (a?.status || "").toUpperCase() === "ACTIVE");
    if (linkedInTasks.length && !hasActiveLi) {
      out.push({
        tone: "bad",
        key: "linkedin_blocked",
        label: "LinkedIn tasks can't run",
        text: "Your LinkedIn session is disconnected, so these tasks will fail.",
        actions: actions.askCopilot
          ? [
              {
                label: "Why?",
                run: () =>
                  actions.askCopilot(
                    "My LinkedIn tasks cannot run because the LinkedIn session is disconnected. Explain what to do, do not change anything."
                  )
              }
            ]
          : []
      });
    }
  }

  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = dayStart.getTime() + DAY;
  const dueToday = list.filter(r => r.task?.taskDue >= dayStart.getTime() && r.task.taskDue < dayEnd);
  if (dueToday.length > 25) {
    out.push({
      tone: "neutral",
      key: "heavy_day",
      label: "Heavy day ahead",
      text: `${dueToday.length} tasks due today. Let me order them by who is warmest.`,
      actions: actions.askCopilot
        ? [
            {
              label: "Prioritize my queue",
              run: () =>
                actions.askCopilot(
                  "Order today's tasks by warmth: replied, then clicked, then opened, then cold. Show the list."
                )
            }
          ]
        : []
    });
  }

  const bounced = list.filter(r => stageKey(r.contact?.stage) === "BOUNCED");
  if (bounced.length) {
    out.push({
      tone: "bad",
      key: "bounced",
      label: plural(bounced.length, "task") + " for a bounced contact",
      text: "Their email bounced, so the task will not land. Skip it or enrich the contact first.",
      actions: actions.askCopilot
        ? [{ label: "Skip them", run: () => actions.askCopilot(skipPrompt(bounced)) }]
        : []
    });
  }

  const byType = {};
  list.forEach(r => {
    const k = r.task?.taskType || "other";
    byType[k] = (byType[k] || 0) + 1;
  });
  const [topType, topCount] = Object.entries(byType).sort((a, b) => b[1] - a[1])[0] || [];
  if (topType && topCount / list.length > 0.6 && list.length >= 4) {
    out.push({
      tone: "neutral",
      key: "dominant_type",
      label: `Mostly ${topType} tasks`,
      text: `${topCount} of ${list.length} are ${topType}. Batch them in one sitting.`,
      actions: actions.filterType ? [{ label: "Only these", run: () => actions.filterType(topType) }] : []
    });
  }

  const optedOut = list.filter(r => ["OPTED_OUT", "DO_NOT_CONTACT"].includes(stageKey(r.contact?.stage)));
  if (optedOut.length) {
    out.push({
      tone: "bad",
      key: "opted_out",
      label: plural(optedOut.length, "task") + " for an opted-out contact",
      text: "They asked not to be contacted. Skip these.",
      actions: actions.askCopilot
        ? [{ label: "Skip them", run: () => actions.askCopilot(skipPrompt(optedOut)) }]
        : []
    });
  }

  const perContact = {};
  list.forEach(r => {
    const id = r.task?.contactId || r.contact?.id;
    if (id) perContact[id] = (perContact[id] || 0) + 1;
  });
  const dupes = Object.values(perContact).filter(n => n > 1).length;
  if (dupes) {
    out.push({
      tone: "neutral",
      key: "duplicate_contact",
      label: plural(dupes, "contact") + " with more than one task",
      text: "One touch per contact per day. Merge or space them out."
    });
  }

  return out;
};

export default taskFindings;
